import React, { Fragment, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

import { ComponentContext } from '../../../context/componentContext';
import { FarmContext } from '../../../context/farmContext';
import { OrderContext } from '../../../context/orderContext';
import { AuthContext } from '../../../context/authContext';

import TaskReceipt from './TaskReceipt';
import ActionButton from '../../helpers/ActionButton';
import { DownloadIcon } from '../../Icons';

const FarmDocument = ({ data }) => {
  const { menuOpen, setUnexpectedError, setSession } = useContext(ComponentContext);
  const { downloadContract } = useContext(FarmContext);
  const { downloadPDF } = useContext(OrderContext);
  const { isAuthenticated } = useContext(AuthContext);

  const [isSubmittingA, setIsSubmittingA] = useState(false);
  const [isSubmittingB, setIsSubmittingB] = useState(false);
  const [isSubmittingC, setIsSubmittingC] = useState(false);

  const { user, token } = isAuthenticated();

  return (
    <Fragment>
      <div className={`flex flex-wrap justify-center ${menuOpen ? 'pl-8 md:pl-24' : ''}`}>
        <div className="w-full md:w-4/5 lg:w-133">
          <div className="bg-cf-300 text-white rounded px-6 py-10 text-center">
            <h4 className="font-semibold">Welcome to your Documents!</h4>
            <p className="text-sm md:text-base">Latest updates on your farm documents.</p>
          </div>
        </div>
      </div>

      <div
        className={`flex flex-wrap justify-center px-4 md:px-0 mt-10 ${
          menuOpen ? 'pl-8 md:pl-24' : ''
        }`}
      >
        <div className="w-full md:w-4/5 lg:w-3/5 mx-auto mt-10 pb-24 px-4 md:px-0">
          <div className="grid grid-cols-1 md:grid-cols-3 md:gap-5">
            <div className="bg-white rounded p-10 flex flex-col mb-20 md:mb-0 justify-center">
              <h3 className="text-left mb-4">Contract</h3>
              <div className="w-40 mx-auto mt-6">
                {!user.signature || !user.signature.string ? (
                  <>
                    <small className="text-xs">Requires signature to be setup</small>
                    <ActionButton
                      name="Download"
                      textColor="text-white"
                      color="bg-cf-300"
                      icon={
                        <DownloadIcon
                          styles={{ margin: '2px 5px 0 0', size: '12px', verticalAlign: 'middle' }}
                        />
                      }
                      click={() => {
                        toast.error('You need to setup your signature in your profile', {
                          position: toast.POSITION.TOP_RIGHT
                        });
                      }}
                      btnPad={'px-4 py-3'}
                    />
                  </>
                ) : (
                  <>
                    {data.farm.contract ? (
                      <a href={data.farm.contract} download>
                        <ActionButton
                          name="Download"
                          textColor="text-white"
                          color="bg-cf-300"
                          icon={
                            <DownloadIcon
                              styles={{
                                margin: '2px 5px 0 0',
                                size: '12px',
                                verticalAlign: 'middle'
                              }}
                            />
                          }
                          btnPad={'px-4 py-3'}
                        />
                      </a>
                    ) : (
                      <ActionButton
                        isSubmitting={isSubmittingA}
                        name="Download"
                        color="bg-cf-300"
                        textColor="text-white"
                        icon={
                          <DownloadIcon
                            styles={{
                              margin: '2px 5px 0 0',
                              size: '12px',
                              verticalAlign: 'middle'
                            }}
                          />
                        }
                        click={async () => {
                          try {
                            setIsSubmittingA(true);
                            const response = await downloadContract(data.farm._id, token);
                            setIsSubmittingA(false);
                            if (response.status === 200) {
                              const pdfBlob = new Blob([response.data], {
                                type: 'application/pdf'
                              });
                              saveAs(
                                pdfBlob,
                                `${data.farm.refKey}-contract-for-${user.firstName}.pdf`
                              );
                              toast.success('Download Completed', {
                                position: toast.POSITION.TOP_RIGHT
                              });
                            } else {
                              toast.error('Failed to download, Retry!', {
                                position: toast.POSITION.TOP_RIGHT
                              });
                            }
                          } catch (error) {
                            if (error && error.status === 401) setSession(false);
                            else setUnexpectedError('Unexpected Server Error!');
                          }
                        }}
                        btnPad={'px-4 py-3'}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="bg-white rounded p-10 flex flex-col mb-20 md:mb-0 justify-center">
              <h3 className="text-left mb-4">Receipt</h3>
              <div className="w-40 mx-auto mt-6">
                {data.farm.receipt ? (
                  <a href={data.farm.receipt} download>
                    <ActionButton
                      name="Download"
                      textColor="text-white"
                      color="bg-cf-300"
                      icon={
                        <DownloadIcon
                          styles={{ margin: '2px 5px 0 0', size: '12px', verticalAlign: 'middle' }}
                        />
                      }
                      btnPad={'px-4 py-3'}
                    />
                  </a>
                ) : (
                  <ActionButton
                    isSubmitting={isSubmittingB}
                    name="Download"
                    textColor="text-white"
                    color="bg-cf-300"
                    icon={
                      <DownloadIcon
                        styles={{ margin: '2px 5px 0 0', size: '12px', verticalAlign: 'middle' }}
                      />
                    }
                    click={async () => {
                      try {
                        setIsSubmittingB(true);
                        const response = await downloadPDF(data.farm.orderId, 'receipt', token);
                        setIsSubmittingB(false);
                        if (response.status === 200) {
                          const pdfBlob = new Blob([response.data], {
                            type: 'application/pdf'
                          });
                          saveAs(pdfBlob, `${data.farm.refKey}-receipt-for-${user.firstName}.pdf`);
                          toast.success('Download Completed', {
                            position: toast.POSITION.TOP_RIGHT
                          });
                        } else {
                          toast.error('Failed to download, Retry!', {
                            position: toast.POSITION.TOP_RIGHT
                          });
                        }
                      } catch (error) {
                        if (error && error.status === 401) setSession(false);
                        else setUnexpectedError('Unexpected Server Error!');
                      }
                    }}
                    btnPad={'px-4 py-3'}
                  />
                )}
              </div>
            </div>
            <div className="bg-white rounded p-10 flex flex-col mb-20 md:mb-0 justify-center">
              <h3 className="text-left mb-4">Invoice</h3>
              <div className="w-40 mx-auto mt-6">
                {data.farm.invoice ? (
                  <a href={data.farm.invoice} download>
                    <ActionButton
                      name="Download"
                      textColor="text-white"
                      color="bg-cf-300"
                      icon={
                        <DownloadIcon
                          styles={{ margin: '2px 5px 0 0', size: '12px', verticalAlign: 'middle' }}
                        />
                      }
                      btnPad={'px-4 py-3'}
                    />
                  </a>
                ) : (
                  <ActionButton
                    isSubmitting={isSubmittingC}
                    name="Download"
                    textColor="text-white"
                    color="bg-cf-300"
                    icon={
                      <DownloadIcon
                        styles={{ margin: '2px 5px 0 0', size: '12px', verticalAlign: 'middle' }}
                      />
                    }
                    click={async () => {
                      try {
                        setIsSubmittingC(true);
                        const response = await downloadPDF(data.farm.orderId, 'invoice', token);
                        setIsSubmittingC(false);
                        if (response.status === 200) {
                          const pdfBlob = new Blob([response.data], {
                            type: 'application/pdf'
                          });
                          saveAs(pdfBlob, `${data.farm.refKey}-invoice-for-${user.firstName}.pdf`);
                          toast.success('Download Completed', {
                            position: toast.POSITION.TOP_RIGHT
                          });
                        } else {
                          toast.error('Failed to download, Retry!', {
                            position: toast.POSITION.TOP_RIGHT
                          });
                        }
                      } catch (error) {
                        if (error && error.status === 401) setSession(false);
                        else setUnexpectedError('Unexpected Server Error!');
                      }
                    }}
                    btnPad={'px-4 py-3'}
                  />
                )}
              </div>
            </div>
          </div>
          {data && data.feeds.length > 0 && (
            <div className="mt-20">
              <h3 className="mb-1">Farm Receipts</h3>
              <p className="mb-5">
                For each completed task, there is a costing attached, here are the receipt.
              </p>
              {data.feeds.map((feed, fIndex) => (
                <div key={feed._id} className="pb-5">
                  <>
                    {feed.tasks.map((task, tIndex) => (
                      <TaskReceipt
                        key={task._id}
                        activityTitle={feed.title}
                        task={task}
                        no={data.farm.refKey + '-' + fIndex + '-' + tIndex}
                        acreage={data.farm.acreage}
                        cropImg={data.farm.product.crop.imageUrl}
                      />
                    ))}
                  </>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default FarmDocument;
